import React from 'react';
import { PARTICLES, HIGGS } from '../data';

const allParticles = [...PARTICLES.filter(p => p.mass > 0 && typeof p.mass === 'number'), HIGGS];
const maxLog = Math.log10(HIGGS.mass);
const minLog = Math.log10(0.511); // electron

export default function MassScale({ onSelect, selected }) {
  const sorted = [...allParticles].sort((a, b) => {
    const ma = typeof a.mass === 'number' ? a.mass : 0;
    const mb = typeof b.mass === 'number' ? b.mass : 0;
    return mb - ma;
  });

  return (
    <div style={{ padding: '0 0 2rem' }}>
      <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.75rem', letterSpacing: '0.1em', marginBottom: '1.5rem', fontFamily: 'Space Mono, monospace', textTransform: 'uppercase' }}>
        Higgs coupling strength → mass (logarithmic scale)
      </p>
      <div style={{ position: 'relative' }}>
        {sorted.map((p, i) => {
          const logMass = Math.log10(typeof p.mass === 'number' ? p.mass : 0.511);
          const pct = ((logMass - minLog) / (maxLog - minLog)) * 100;
          const isSel = selected?.id === p.id;
          const catColor = p.category === 'quark' ? '#4a9eff'
            : p.category === 'lepton' ? '#50e0a0'
            : p.category === 'higgs' ? '#f0c040'
            : '#ff9040';

          return (
            <div
              key={p.id}
              onClick={() => onSelect(p)}
              style={{
                display: 'flex', alignItems: 'center', gap: '0.75rem',
                marginBottom: '0.5rem', cursor: 'pointer',
                padding: '0.3rem 0.5rem',
                borderRadius: '6px',
                background: isSel ? 'rgba(255,255,255,0.06)' : 'transparent',
                transition: 'background 0.15s',
              }}
            >
              <div style={{
                width: '52px', fontSize: '0.7rem', textAlign: 'right',
                color: catColor, fontFamily: 'Space Mono, monospace',
                flexShrink: 0, opacity: 0.85,
              }}>
                {p.symbol}
              </div>
              <div style={{ flex: 1, position: 'relative', height: '6px' }}>
                <div style={{
                  position: 'absolute', left: 0, top: 0, height: '100%',
                  width: `${Math.max(1.5, pct)}%`,
                  background: catColor,
                  borderRadius: '3px',
                  opacity: isSel ? 1 : 0.55,
                  transition: 'opacity 0.15s, width 0.4s',
                  boxShadow: isSel ? `0 0 8px ${catColor}` : 'none',
                }} />
              </div>
              <div style={{
                width: '80px', fontSize: '0.68rem', color: 'rgba(255,255,255,0.5)',
                fontFamily: 'Space Mono, monospace', flexShrink: 0,
              }}>
                {p.massLabel}
              </div>
            </div>
          );
        })}

        {/* Massless particles */}
        <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
          <p style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.3)', fontFamily: 'Space Mono', marginBottom: '0.5rem' }}>
            No Higgs coupling → massless
          </p>
          {[
            { id: 'photon', symbol: 'γ', name: 'Photon', color: '#ffffff' },
            { id: 'gluon', symbol: 'g', name: 'Gluon', color: '#e060ff' },
          ].map(p => (
            <div
              key={p.id}
              onClick={() => onSelect(PARTICLES.find(x => x.id === p.id))}
              style={{
                display: 'flex', alignItems: 'center', gap: '0.75rem',
                marginBottom: '0.4rem', cursor: 'pointer', padding: '0.3rem 0.5rem', borderRadius: '6px',
                background: selected?.id === p.id ? 'rgba(255,255,255,0.06)' : 'transparent',
              }}
            >
              <div style={{ width: '52px', textAlign: 'right', color: p.color, fontFamily: 'Space Mono', fontSize: '0.7rem', opacity: 0.85 }}>
                {p.symbol}
              </div>
              <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.3)' }}>— — — — — massless</div>
            </div>
          ))}
          {/* Neutrinos */}
          {PARTICLES.filter(p => p.id.startsWith('nu_')).map(p => (
            <div
              key={p.id}
              onClick={() => onSelect(p)}
              style={{
                display: 'flex', alignItems: 'center', gap: '0.75rem',
                marginBottom: '0.4rem', cursor: 'pointer', padding: '0.3rem 0.5rem', borderRadius: '6px',
                background: selected?.id === p.id ? 'rgba(255,255,255,0.06)' : 'transparent',
              }}
            >
              <div style={{ width: '52px', textAlign: 'right', color: '#50e0a0', fontFamily: 'Space Mono', fontSize: '0.7rem', opacity: 0.6 }}>
                {p.symbol}
              </div>
              <div style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.25)', fontStyle: 'italic' }}>mass uncertain — beyond Standard Model</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
